import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { write } from '../language/lang.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { html } from 'lit';

/**
 * How a caller's markdown is sanitised. `keptPeople` is set only by a caller drawing kept text the
 * server wrote (`keptTextMarkdown` output), where a person is a `<user-name userid>` element; any
 * other markdown, a note a person typed above all, has that element removed, so a typed one can
 * never draw as someone's name with their card (U50 review 1 F1).
 */
export interface MarkdownOptions {
  keptPeople?: boolean;
}

export function convertToMarkdown(
  data: string | string[] | undefined | null,
  trimBlanks = true,
  options: MarkdownOptions = {}
): string {
  if (!data) return '';

  if (trimBlanks) {
    const newdata = typeof data === 'string' ? data.split('\n') : data;
    data = newdata
      .map((line) => {
        let newLine = line.trimStart();
        while (newLine.trimStart().startsWith('++')) {
          newLine = newLine.replace('++', '  +');
        }

        return newLine;
      })
      .join('\n');
  } else {
    data = typeof data === 'string' ? data : data.join('\n');
  }
  return sanitiseMarkdownHtml(
    marked.parse(data, { async: false }),
    options.keptPeople === true
  );
}

/**
 * Marked's HTML with everything that could run removed (U49). Markdown here includes audit notes
 * behind the `@#MD` marker, which are text a person typed, and marked passes raw HTML through, so
 * without this an `<img onerror>`, a `<script>` or a `javascript:` link in a note ran for everyone
 * who opened the trail. Sanitised here, where marked's output is produced, so no caller can reach
 * unsafeHTML with unsanitised markdown.
 *
 * DOMPurify's defaults keep ordinary markup (emphasis, lists, links, headings, tables) and remove
 * scripts, frames, event handlers and script URLs. The one addition, for kept text only
 * (`keptPeople`), is the element kept notes carry for a person (`keptTextMarkdown`): `<user-name>`
 * is allowed and `userid` is added to the attributes it may carry; the default inert attributes
 * (title, class and the like) still apply to it as to any element, and a handler never does.
 * Without `keptPeople` it is removed like any unknown element: a person's own note typing it would
 * otherwise draw as another person's card (U50 review 1 F1).
 *
 * Also removed, though they run nothing: a form and its submitting controls, a style element and a
 * style attribute, a dialog, and the popover attributes. Markdown never produces them, and in a
 * note they would let the author plant a form posting off-site, or a panel laid over the page that
 * can pass for the app's own prompt, in every reader's trail. `input` is kept, because marked draws
 * a task list item's checkbox with it, and without a form or a popover target it sends and opens
 * nothing.
 */
function sanitiseMarkdownHtml(html: string, keptPeople: boolean): string {
  return DOMPurify.sanitize(html, {
    FORBID_TAGS: [
      'form',
      'button',
      'select',
      'option',
      'textarea',
      'style',
      'dialog',
    ],
    FORBID_ATTR: ['style', 'popover', 'popovertarget', 'popovertargetaction'],
    ...(keptPeople
      ? {
          CUSTOM_ELEMENT_HANDLING: {
            tagNameCheck: /^user-name$/,
            attributeNameCheck: /^userid$/,
            allowCustomizedBuiltInElements: false,
          },
        }
      : {}),
  });
}

export function convertToMarkdownHTML(
  data: string | string[] | undefined | null,
  trimBlanks = true
) {
  return unsafeHTML(convertToMarkdown(data, trimBlanks));
}

export function convertToMarkdownTemplateResult(
  data: string | string[] | undefined | null,
  trimBlanks = true,
  options: MarkdownOptions = {}
) {
  return html`${unsafeHTML(convertToMarkdown(data, trimBlanks, options))}`;
}

export function formatStringsAsMarkdownList(data: string[] | null | undefined) {
  return (data ?? []).map((x) => `+ ${x}`).join('\n');
}

export function writeMdHTML(
  template: TemplateStringsArray,
  ...args: unknown[]
) {
  const data = write(template, ...args);
  return convertToMarkdownHTML(data);
}

export function markdown(template: TemplateStringsArray, ...args: unknown[]) {
  const data = write(template, ...args);
  return convertToMarkdownTemplateResult(data);
}
